import React, { useEffect, useState, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import TagManager from 'react-gtm-module';
import { FormattedMessage } from 'react-intl';
import { push as pushToHistory } from 'redux-first-history';
import { ThemeProvider } from 'styled-components';

// Components
import ConnectedIntlProvider from '../utils/ConnectedIntlProvider';
import FooterContainer from '../containers/common/FooterContainer';
import HeaderContainer from '../containers/common/HeaderContainer';
import HyperDashboard from './hyperDashboard/HyperDashboard';
import ModalContainer from '../containers/common/ModalContainer';
import SpinnerContainer from '../containers/common/SpinnerContainer';
import SystemErrorMessage from './ssap/notifications/SystemErrorMessage';
import Feedback from './common/feedback/Feedback';
// ActionCreators
import {
    acknowledgeTransferStatus,
    getEnvironment,
    fetchMockModal,
    fetchPingStatus,
    fetchUnreadMessages,
    setError,
} from '../actions/common/actionCreators';

// Hooks

import { useLocalStorage } from '../hooks/useLocalStorage';
import { useLocation } from '../hooks/useLocation';
import { useMockModal } from '../hooks/useMockModal';
import { useNavigation } from '../hooks/useNavigation';

// Constants

import { ALL_USER_ROLES } from '../constants/roles/userRoleTypes';
import { configs } from '../constants/configs/configs';

// Font awesome react setup

import '../constants/configs/FontAwesomeConfig';

// GTM setup

import GTagManager from '../services/gtmConfig';

// routes

import MemberPortalRoutes from '../routes/MemberPortalRoutes';
import { GARoutes } from '../routes/GARoutes';

// Modules

import { GeorgiaHeader } from '../modules/georgia-access';

//hooks
import useSessionIdle from '../hooks/useSessionIdle';

const App = props => {
    const [isStyleLoaded, setStyleLoaded] = useState(!global.__ENV__.PRODUCTION);
    const [environment, setEnvironment] = useState(null);
    const { ASSISTERENROLLMENTENTITY, ASSISTER } = ALL_USER_ROLES;
    const roleSpecificSurveyMonkeyLink = [ASSISTERENROLLMENTENTITY, ASSISTER];
    const [theme, setTheme] = useState({});

    const {
        actions,
        actions: { fetchMockModal, pushToHistory },
        adjustedFlow,
        data,
        page,
    } = props;

    const {
        endpoints,
        externalEndpoints: { GENERAL, ID_LINKS },
    } = configs;

    useNavigation(pushToHistory);

    const { initSessionIdle } = useSessionIdle();
    const { currentLocation } = useLocation();

    useLocalStorage();
    useMockModal({
        adjustedFlow,
        page,
        data,
        fetchMockModal,
    });

    useEffect(() => {
        actions.getEnvironment();
    }, []);

    useEffect(() => {
        async function loadThemeData(stateCode) {
            try {
                // Dynamically import the CSS as a JS object (assuming CSS Modules setup)
                const themeModule = configs.ENV.PRODUCTION
                    ? await fetch(`${window.location.origin}/hix/resources/spa/build/assets/css/index_${stateCode}.module.css`)
                        .then(async data => {
                            const cssText = await data.text();
                            const exportBlock = Array.from(cssText.matchAll(/:export\s*{([^}]*)}/gs)).map(match => {
                                return match[1].trim();
                            });
                            if (exportBlock) {
                                const variables = exportBlock.reduce((prevVars, block) => {
                                    const blockVars = block
                                        .split(';')
                                        .filter(Boolean)
                                        .reduce((vars, line) => {
                                            const [key, value] = line.split(':').map(s => s.trim());
                                            vars[key] = value;
                                            return vars;
                                        }, {});
                                    return { ...prevVars, ...blockVars };
                                }, {});
                                return variables;
                            }
                        })
                        .catch(error => {
                            console.log('err', error);
                        })
                    : (await import(`../../build/assets/css/index_${stateCode}.module.css`)).default;
                setTheme(themeModule || {});
                // `themeModule` will have the exported CSS properties
            } catch (error) {
                console.error(
                    'Failed to load theme data:',
                    error,
                    `path: ${window.location.origin}/hix/resources/spa/build/assets/css/index_${stateCode}.module.css`,
                );
            }
        }
        let interval = null;
        function loadStyles(stateCode) {
            if (stateCode && configs.ENV.PRODUCTION) {
                const isFireFox = typeof InstallTrigger !== 'undefined';
                if (isFireFox) {
                    const style = document.createElement('style');
                    if (configs.ENV.CUSTOM) {
                        style.textContent = `@import "/build/css/index_${stateCode}.css"`;
                    } else {
                        style.textContent = `@import "/hix/resources/spa/build/assets/css/index_${stateCode}.module.css"`;
                    }

                    const fi = setInterval(() => {
                        try {
                            style.sheet.cssRules;
                            setStyleLoaded(true);
                            clearInterval(fi);
                        } catch (err) {
                            console.log('In App: error in loading css, err = ', err);
                        }
                    }, 500);

                    document.querySelector('head').appendChild(style);
                } else {
                    const link = document.createElement('link');
                    link.setAttribute('rel', 'stylesheet');
                    if (configs.ENV.CUSTOM) {
                        link.setAttribute('href', `/build/css/index_${stateCode}.css`);
                    } else {
                        link.setAttribute('href', `/hix/resources/spa/build/assets/css/index_${stateCode}.module.css`);
                    }
                    link.addEventListener('load', () => {
                        setTimeout(() => {
                            setStyleLoaded(true);
                        }, 500);
                    });
                    document.querySelector('head').appendChild(link);

                    const ti = setInterval(() => {
                        try {
                            if (link.sheet && link.sheet.cssRules.length > 0) {
                                clearInterval(ti);
                            } else if (link.styleSheet && link.styleSheet.cssText.length > 0) {
                                clearInterval(ti);
                            } else if (link.innerHTML && link.innerHTML.length > 0) {
                                clearInterval(ti);
                            }
                        } catch (err) {
                            console.log('In App: error in loading css, err = ', err);
                            setStyleLoaded(true);
                            clearInterval(ti);
                        }
                    });
                }
            } else {
                if (stateCode) {
                    const link = document.createElement('link');
                    link.setAttribute('rel', 'stylesheet');
                    link.setAttribute('href', `/build/assets/css/index_${stateCode}.module.css`);
                    document.querySelector('head').appendChild(link);
                }
            }
        }

        function initTagManager(gaContainerId) {
            if (gaContainerId !== null && gaContainerId) {
                TagManager.initialize({ gtmId: gaContainerId });
            }
        }

        function initializeYHIChat(stateCode) {
            if (stateCode === 'ID' && environment && !environment.devEnvironment) {
                const script = document.createElement('script');
                script.src = 'https://sharpenchat.iz1.sharpen.cx';
                script.setAttribute(
                    'appKey',
                    'D189D445901F97206794420A773B73245BE20279398C0BD736B78C2035CB8F0DAE0A18441D128F9B',
                );
                script.async = true;
                document.body.appendChild(script);
            }
        }

        if (props.environment) {
            const {
                anonymous,
                clientInactivityThreshold,
                links: { LOGOUT: logoutUrl },
                popupThresholdValue,
                properties: { GOOGLE_ANALYTICS_CONTAINER_ID },
                stateCode,
            } = props.environment;

            initSessionIdle(anonymous, clientInactivityThreshold, popupThresholdValue, logoutUrl);
            initTagManager(GOOGLE_ANALYTICS_CONTAINER_ID);
            initializeYHIChat(stateCode);
            setEnvironment(props.environment);
            if (environment?.stateCode !== stateCode){
                loadStyles(stateCode);
                loadThemeData(stateCode);
            }
        }

        return () => clearInterval(interval);
    }, [props.environment]);

    useEffect(() => {
        if (environment && environment.anonymous === false) {
            let stopped = false;
            const interval = setInterval(() => {
                !stopped && actions.fetchUnreadMessages(endpoints.UNREAD_MESSAGE_COUNT, {});
                if (global.__ENV__.DEVELOPMENT) {
                    stopped = true;
                }
            }, 30000);
            return () => clearInterval(interval);
        }
    }, [environment]);

    function openSurveyMonkey(stateCode) {
        const hasSurveyMonkeyLink = roleSpecificSurveyMonkeyLink.includes(environment.user.role.toUpperCase());
        const stateCodeMap = stateCode === 'ID' ? ID_LINKS : GENERAL;
        const link = hasSurveyMonkeyLink ? stateCodeMap.ROLE_BASED_LINK : stateCodeMap.DEFAULT_SURVEY_LINK;
        window.open(link, '_blank');
    }

    function renderGAPortal() {
        return (
            <Fragment>
                <GeorgiaHeader {...{ environment: environment }} />
                <SystemErrorMessage>
                    <GARoutes />
                </SystemErrorMessage>
                <FooterContainer {...{ stateCode: environment.stateCode }} />
            </Fragment>
        );
    }

    function renderMemberPortal() {
        return (
            <Fragment>
                <HeaderContainer {...{ stateCode: environment.stateCode }} />
                <SystemErrorMessage>
                    <MemberPortalRoutes
                        {...{
                            stateCode: environment.stateCode,
                            ticketEnabled: environment.ticketEnabled,
                            userData: environment.user,
                            leadPresent: environment.screenerConfigs.leadPresent,
                        }}
                    />
                </SystemErrorMessage>
                <FooterContainer {...{ stateCode: environment.stateCode }} />
            </Fragment>
        );
    }

    if (!environment) {
        return null;
    }

    if (!isStyleLoaded) {
        return (
            <ConnectedIntlProvider>
                <div>
                    <FormattedMessage id="common.loading" />
                </div>
            </ConnectedIntlProvider>
        );
    }

    const isGeorgiaLite =
        environment &&
        environment.isState.GA &&
        ((environment.user && environment.user.role === ALL_USER_ROLES.INDIVIDUAL_LITE) || environment.anonymous);


    return (
        <ConnectedIntlProvider>
            <ThemeProvider theme={theme}>
                <ModalContainer />
                <SpinnerContainer />
                <Feedback
                    ALL_USER_ROLES={ALL_USER_ROLES}
                    environment={environment}
                    openSurveyMonkey={openSurveyMonkey}
                />
                <HyperDashboard {...{ currentLocation }} />
                <GTagManager
                    user={environment.user}
                    GOOGLE_ANALYTICS_CONTAINER_ID={environment.properties.GOOGLE_ANALYTICS_CONTAINER_ID}
                />
                {isGeorgiaLite && renderGAPortal()}
                {!isGeorgiaLite && renderMemberPortal()}
            </ThemeProvider>
        </ConnectedIntlProvider>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,
    messages: state.common.messages,
    adjustedFlow: state.ssap.adjustedFlow,
    data: state.ssap.data,
    page: state.ssap.page,
    methods: state.ssap.methods,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            fetchPingStatus,
            fetchUnreadMessages,
            fetchMockModal,
            setError,
            acknowledgeTransferStatus,
            getEnvironment,
            pushToHistory,
        },
        dispatch,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
